import { useState } from 'react';
import { useGame, useS, getLocalRole } from '../store/game';
import { Panel, Stat, Seg } from '../components/ui';
import { TrendNote } from '../components/Trend';
import { AX, AXN, SCALE } from '../content/axes';
import { pub, scaleLabel } from '../engine/opinion';
import { ownPos, wendeUsed } from '../engine/review';
import { WENDE_MAX, WENDE_COST } from '../engine/zeitgeist';
import { BilanzTable } from './End';
import { de, sg } from '../engine/util';
import { timeRole, isVersus } from '../engine/versus';
import { canUpgrade } from '../engine/upgrade';
import { RULES, RULE_NOTES, verOf } from '../engine/version';
import { S } from '../engine/state';

const R2: Record<string, string> = { kanzler: 'Kanzlerpartei', partner: 'Koalitionspartner', opposition: 'Opposition' };

/** Neuere Spielregeln für die laufende Partie übernehmen (nur Einzelspieler, nur hier zwischen den Legislaturen). */
function RulesUpgrade() {
  useS(); const { dispatch } = useGame();
  if (!canUpgrade()) return null;
  const from = verOf(S), notes = Object.keys(RULE_NOTES).map(Number).filter((v) => v > from && v <= RULES).flatMap((v) => RULE_NOTES[v]);
  return <Panel title="Neue Spielregeln verfügbar" className="upgrade">
    <p>Diese Partie läuft noch nach den Regeln, mit denen sie begonnen hat. Seitdem ist das Spiel erweitert worden. Du kannst die neuen Regeln ab der nächsten Legislatur übernehmen:</p>
    <ul className="plain">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
    <p className="sub">Bisherige Ergebnisse bleiben, wie sie sind. Die Partie zählt weiter für die Bestenliste. Der Umstieg lässt sich nicht rückgängig machen.</p>
    <div className="actions"><button type="button" className="yes small" onClick={() => dispatch('upgradeRules', RULES)}>Neue Regeln übernehmen</button></div>
  </Panel>;
}

/** Rückblick am Ende der Legislatur, bevor der Wahlkampf beginnt. */
export function Review() {
  const S = useS(); const { dispatch } = useGame(); const r = S.review;
  if (!r) return null;
  const versus = isVersus();
  const mayGo = !versus || timeRole() === getLocalRole();
  const kept = r.promises.filter((p) => p.state === 'kept').length;
  const ok = r.budgets.filter((b) => b.ok).length;
  return (
    <section className="screen">
      <div><span className="label">{r.leg + 1}. Legislatur · {r.from}–{r.to}{r.early ? ' · vorzeitig beendet' : ''}</span>
        <h2>Rückblick: Was hat die Regierung geschafft?</h2></div>
      <p>{r.chan} regierte {r.gov.length > 1 ? `in einer Koalition aus ${r.gov.join(', ')}` : r.minority ? 'als Minderheitsregierung' : 'allein'}. {r.laws.length ? `${r.laws.length} ${r.laws.length === 1 ? 'Gesetz ist' : 'Gesetze sind'} in Kraft getreten` : 'Kein neues Gesetz ist in Kraft getreten'}{r.repealed.length ? `, ${r.repealed.length} aufgehoben` : ''}. Danach beginnt der Wahlkampf.</p>
      <div className="stats" aria-label="Bilanz">
        <Stat label="Gesetze" value={String(r.laws.length)} />
        <Stat label="Umfrage" value={de(r.poll1) + ' %'} delta={r.share0 ? r.poll1 - r.share0 : null} unit=" Pkt" icon="poll" sub={r.share0 ? `Wahl: ${de(r.share0)} %` : undefined} />
        <Stat label="Schulden" value={Math.round(r.debt1).toLocaleString('de-DE') + ' Mrd €'} delta={r.debt1 - r.debt0} goodUp={false} unit=" Mrd" icon="debt" />
        <Stat label="Saldo" value={sg(r.saldo) + ' Mrd €'} bad={r.saldo < 0} icon="budget" />
        <Stat label="Haushalte" value={`${ok} von ${r.budgets.length}`} bad={ok < r.budgets.length} sub="beschlossen" />
        <Stat label="Krisen · Affären" value={`${r.crises} · ${r.scandals}`} />
      </div>
      <div className="cols">
        <div>
          <Panel title="In Kraft getreten">
            {r.laws.length ? <ul className="plain">{r.laws.map((L, i) => <li key={i}>{L.t}{L.by ? <span className="sub"> · Entwurf {L.by}</span> : null}</li>)}</ul> : <p className="sub">Keine neuen Gesetze in dieser Legislatur.</p>}
            {r.repealed.length > 0 && <><span className="label">Aufgehoben</span><ul className="plain">{r.repealed.map((t, i) => <li key={i}>{t}</li>)}</ul></>}
            {r.voided.length > 0 && <><span className="label">Vom Verfassungsgericht gekippt</span><ul className="plain">{r.voided.map((t, i) => <li key={i} className="down">{t}</li>)}</ul></>}
          </Panel>
          <BilanzTable />
        </div>
        <div>
          {r.promises.length > 0 && <Panel title={`Wahlversprechen · ${kept} von ${r.promises.length} gehalten`}>
            <ul className="plain">{r.promises.map((p, i) => <li key={i} className={p.state === 'kept' ? 'up' : 'down'}>{p.state === 'kept' ? '✓' : '✗'} {p.t}</li>)}</ul>
          </Panel>}
          {r.budgets.length > 0 && <Panel title="Haushalte">
            <ul className="plain">{r.budgets.map((b, i) => <li key={i} className={b.ok ? '' : 'down'}>{b.y}: {b.ok ? 'beschlossen' : 'gescheitert'}, Saldo {sg(b.saldo)} Mrd €</li>)}</ul>
          </Panel>}
          {r.players && <Panel title="Die Spieler in dieser Legislatur">
            <ul className="plain">{r.players.map((p) => <li key={p.role}><b>{p.name}</b> <span className="sub">· {R2[p.role2] || p.role2}{p.role === getLocalRole() ? ' · ihr' : ''}</span><br />
              <span className="sub">{p.role2 === 'kanzler' ? 'Hat die Regierung geführt.' : `${p.d.billsOk} von ${p.d.bills} Anträgen durch, ${p.d.suitsWon} von ${p.d.suits} Klagen gewonnen, ${p.d.attacks} Aktuelle Stunden${p.d.misstrauen ? `, ${p.d.misstrauen} Misstrauensvoten` : ''}.`}</span></li>)}</ul>
          </Panel>}
        </div>
      </div>
      <ProgramTune />
      <RulesUpgrade />
      <div className="actions">
        {mayGo ? <button type="button" className="yes" onClick={() => dispatch('reviewDone')}>Wahlkampf starten</button>
          : <span className="sub">Die andere Partei startet den Wahlkampf, sobald sie den Rückblick gelesen hat.</span>}
      </div>
    </section>
  );
}

/** Stimmungsbarometer und Programm nachschärfen, vor dem nächsten Wahlkampf. */
function ProgramTune() {
  const S = useS(); const { dispatch } = useGame(); const [all, setAll] = useState(false);
  const versus = isVersus(), role = getLocalRole();
  if (versus && !S.mp!.pl[role].party) return null;
  const pos = ownPos(versus ? role : undefined), used = wendeUsed(versus ? role : undefined), p0 = S.review?.pos0?.[versus ? role : 'YOU'] || {};
  const moved = (a: string) => Math.abs(pub(a) - (S.pubE?.[a] ?? pub(a)));
  const sorted = [...(AX as string[])].sort((a, b) => moved(b) - moved(a));
  const list = all ? sorted : sorted.filter((a, i) => i < 6 || pos[a] !== p0[a]);
  return (
    <Panel title="Stimmung im Land · Programm nachschärfen" aside={<span className={'num ' + (used ? 'down' : '')}>{used} von {WENDE_MAX} Stufen</span>}>
      <p className="sub">Die Meinung der Bevölkerung hat sich seit der letzten Wahl verschoben. Ihr dürft euer Programm um bis zu {WENDE_MAX} Stufen anpassen. Jede Stufe kostet im Wahlkampf {de(WENDE_COST)} Punkte Glaubwürdigkeit{used ? `, derzeit ${sg(-used * WENDE_COST)} Punkte` : ''}. Dafür passt euer Programm danach womöglich besser zur Stimmung.</p>
      {list.map((a) => (
        <div className="q" key={a}><span className="label">{AXN[a]}{pos[a] !== p0[a] ? <span className="down"> · geändert</span> : null}</span>
          <span className="sub">Bevölkerung: <b>{scaleLabel(pub(a))}</b></span>
          <TrendNote a={a} y={pos[a] ?? 0} />
          <Seg value={pos[a] ?? 0} options={SCALE} onChange={(v) => dispatch('reposition', a, v)} />
        </div>
      ))}
      {!all && sorted.length > list.length && <div className="actions"><button type="button" className="small" onClick={() => setAll(true)}>Alle {sorted.length} Themen zeigen</button></div>}
    </Panel>
  );
}
